import React from 'react';
import { Palette, Eye, Grid as GridIcon, Type as TypeIcon } from 'lucide-react';
import { ChartConfig } from '../../types';
import { COLOR_PALETTES } from '../../constants';

interface HorizontalStyleBarProps {
    config: ChartConfig;
    onCustomizationChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

export const HorizontalStyleBar: React.FC<HorizontalStyleBarProps> = ({
    config,
    onCustomizationChange
}) => {
    const customization = config.customization || {};

    return (
        <div className="h-16 glass-panel border-t border-white/10 flex items-center px-6 gap-8 overflow-x-auto custom-scrollbar flex-shrink-0">
            {/* Color Palette */}
            <div className="flex items-center gap-3 flex-shrink-0">
                <Palette className="w-4 h-4 text-gold-500" />
                <label className="text-xs font-semibold text-gray-400 whitespace-nowrap">Palette</label>
                <select
                    name="paletteName"
                    value={customization.paletteName || 'vibrant'}
                    onChange={onCustomizationChange}
                    className="text-xs px-2 py-1 bg-obsidian-800 border border-gold-500/20 rounded text-gray-200"
                >
                    {Object.keys(COLOR_PALETTES)
                        .filter(name => {
                            const isForcePalette = name.startsWith('force_');
                            if (config.type === 'force-directed') {
                                return isForcePalette || !name.startsWith('force_'); // Show all or just force? User said "force palettes only in the force graph", usually implies force palettes shouldn't be elsewhere.
                            }
                            return !isForcePalette;
                        })
                        .map(name => (
                            <option key={name} value={name}>
                                {name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' ')}
                            </option>
                        ))}
                </select>
            </div>

            {/* Vertical Divider */}
            <div className="h-8 w-px bg-white/10 flex-shrink-0" />

            {/* Display Options */}
            <div className="flex items-center gap-4 flex-shrink-0">
                <Eye className="w-4 h-4 text-gold-500" />
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        name="showLegend"
                        checked={customization.showLegend !== false}
                        onChange={onCustomizationChange}
                        className="h-3.5 w-3.5 rounded border-white/20 bg-white/5 text-gold-500"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">Legend</span>
                </label>
                {customization.showLegend !== false && (
                    <select
                        name="legendPosition"
                        value={customization.legendPosition || 'right'}
                        onChange={onCustomizationChange}
                        className="text-[10px] px-1 py-0.5 bg-obsidian-800 border border-gold-500/20 rounded text-gray-300 w-20"
                        title="Legend Position"
                    >
                        <option value="right">Right</option>
                        <option value="top">Top</option>
                        <option value="bottom">Bottom</option>
                        <option value="left">Left</option>
                        <option value="top-left">TL (Overlay)</option>
                        <option value="top-right">TR (Overlay)</option>
                        <option value="bottom-left">BL (Overlay)</option>
                        <option value="bottom-right">BR (Overlay)</option>
                    </select>
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        name="showDataLabels"
                        checked={customization.showDataLabels || false}
                        onChange={onCustomizationChange}
                        className="h-3.5 w-3.5 rounded border-white/20 bg-white/5 text-gold-500"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">Labels</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        name="showGrid"
                        checked={customization.showGrid !== false}
                        onChange={onCustomizationChange}
                        className="h-3.5 w-3.5 rounded border-white/20 bg-white/5 text-gold-500"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">Grid</span>
                </label>
            </div>

            {/* Vertical Divider */}
            <div className="h-8 w-px bg-white/10 flex-shrink-0" />

            {/* Axis Labels */}
            <div className="flex items-center gap-3 flex-shrink-0">
                <TypeIcon className="w-4 h-4 text-gold-500" />
                <input
                    type="text"
                    name="xAxisLabel"
                    value={customization.xAxisLabel || ''}
                    onChange={onCustomizationChange}
                    placeholder="X-Axis"
                    className="w-24 text-xs px-2 py-1 bg-obsidian-800 border border-gold-500/20 rounded text-gray-200 placeholder-gray-500"
                />
                <input
                    type="text"
                    name="yAxisLabel"
                    value={customization.yAxisLabel || ''}
                    onChange={onCustomizationChange}
                    placeholder="Y-Axis"
                    className="w-24 text-xs px-2 py-1 bg-obsidian-800 border border-gold-500/20 rounded text-gray-200 placeholder-gray-500"
                />
            </div>

            {/* Type-specific controls */}
            {['bar'].includes(config.type || '') && (
                <>
                    <div className="h-8 w-px bg-white/10 flex-shrink-0" />
                    <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                        <input
                            type="checkbox"
                            name="roundedBars"
                            checked={customization.roundedBars || false}
                            onChange={onCustomizationChange}
                            className="h-3.5 w-3.5 rounded border-white/20 bg-white/5 text-gold-500"
                        />
                        <span className="text-xs text-gray-400 whitespace-nowrap">Rounded</span>
                    </label>
                </>
            )}

            {['line'].includes(config.type || '') && (
                <>
                    <div className="h-8 w-px bg-white/10 flex-shrink-0" />
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 whitespace-nowrap">Line Width</span>
                        <input
                            type="range"
                            name="lineWidth"
                            min="1"
                            max="5"
                            value={customization.lineWidth || 2}
                            onChange={onCustomizationChange}
                            className="w-20"
                        />
                        <span className="text-xs text-gray-500">{customization.lineWidth || 2}px</span>
                    </div>
                </>
            )}

            {['area'].includes(config.type || '') && (
                <>
                    <div className="h-8 w-px bg-white/10 flex-shrink-0" />
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 whitespace-nowrap">Opacity</span>
                        <input
                            type="range"
                            name="areaOpacity"
                            min="0.1"
                            max="1"
                            step="0.1"
                            value={customization.areaOpacity ?? 0.8}
                            onChange={onCustomizationChange}
                            className="w-20"
                        />
                        <span className="text-xs text-gray-500">{Number(customization.areaOpacity ?? 0.8).toFixed(1)}</span>
                    </div>
                </>
            )}
        </div>
    );
};
